import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The ✓/✗ chip shared by the Story Stage (spec 010 §2) and the homepage Course Card
 * (HERO-V2). One flight: linear x, ease-in y, fade/scale life. Transform + opacity only.
 *
 * The chip is positioned absolutely at (`x`, `y`) inside its nearest positioned
 * ancestor (centre of the 24px disc) and flies by (`dx`, `dy`). Measuring those
 * numbers is the caller's job, and must happen after layout settles.
 */
export interface StoryChipProps {
  ok: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
  delayMs: number;
  flightMs: number;
}

export function StoryChip({ ok, x, y, dx, dy, delayMs, flightMs }: StoryChipProps) {
  const delay = `${Math.round(delayMs)}ms`;
  const flight = `${flightMs}ms`;
  return (
    <span
      aria-hidden
      data-testid="story-chip"
      className="pointer-events-none absolute z-20"
      style={{
        left: x - 12,
        top: y - 12,
        ['--dx' as string]: `${dx}px`,
        ['--dy' as string]: `${dy}px`,
      }}
    >
      <span className="ss-chip-x block" style={{ animationDelay: delay, animationDuration: flight }}>
        <span className="ss-chip-y block" style={{ animationDelay: delay, animationDuration: flight }}>
          <span
            className={cn(
              'ss-chip-life flex h-6 w-6 items-center justify-center rounded-full text-white shadow-md',
              ok ? 'bg-emerald-500' : 'bg-rose-500',
            )}
            style={{ animationDelay: delay, animationDuration: flight }}
          >
            {ok ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <X className="h-3.5 w-3.5" strokeWidth={3} />}
          </span>
        </span>
      </span>
    </span>
  );
}

/** The chip's keyframes + classes. StoryStage folds this into its own stylesheet. */
export const STORY_CHIP_CSS = `
@keyframes ss-chip-x { from { transform: translateX(0); } to { transform: translateX(var(--dx)); } }
@keyframes ss-chip-y { from { transform: translateY(0); } to { transform: translateY(var(--dy)); } }
@keyframes ss-chip-life { 0% { opacity: 0; scale: .5; } 25% { opacity: 1; scale: 1; } 75% { opacity: 1; scale: 1; } 100% { opacity: 0; scale: .7; } }
.ss-chip-x { animation: ss-chip-x both linear; }
.ss-chip-y { animation: ss-chip-y both cubic-bezier(.55, 0, .95, .6); }
.ss-chip-life { animation: ss-chip-life both ease-out; }
@media (prefers-reduced-motion: reduce) {
  .ss-chip-x, .ss-chip-y, .ss-chip-life { animation: none !important; }
}
`;

/** For consumers outside StoryStage: mounts the chip stylesheet once. */
export function StoryChipStyles() {
  return <style>{STORY_CHIP_CSS}</style>;
}
